import { OverlayTrigger } from "react-bootstrap";
import { MdMailOutline } from "react-icons/md";
import { RenderDataSources } from "../../../../../components/common/RenderTooltip";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import type { InboxProvider } from "../../../api/connectionHub.types";
import { PROVIDER_LOGO_URLS } from "../../../constants/providerLogos.constants";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import styles from "./InboxIdentity.module.scss";

const MAX_VISIBLE_COVERS = 3;
const COLLAPSED_COVERS = 2;

export interface InboxIdentityCover {
  key: string;
  logo?: string | null;
  name: string;
}

export interface InboxIdentityProps {
  provider: InboxProvider;
  email: string;
  covers?: InboxIdentityCover[];
}

const CoversRow = ({ covers }: { covers: InboxIdentityCover[] }): JSX.Element => {
  const collapsed = covers.length > MAX_VISIBLE_COVERS;
  const visible = collapsed ? covers.slice(0, COLLAPSED_COVERS) : covers;
  const names = covers.map((cover) => cover.name);

  const row = (
    <div
      className={styles.covers}
      {...(collapsed ? { tabIndex: 0, "aria-label": `Covers: ${names.join(", ")}` } : {})}
    >
      <span>Covers:</span>
      {visible.map((cover) => (
        <span className={styles.cover} key={cover.key}>
          <ConnectionLogo logoUrl={cover.logo} className={styles.coverLogo} />
          <TruncateTooltip text={cover.name} className={styles.coverName} />
        </span>
      ))}
      {collapsed ? <span className={styles.more}>& {covers.length - COLLAPSED_COVERS} others</span> : null}
    </div>
  );

  if (!collapsed) return row;

  const logosByName = Object.fromEntries(covers.map((cover) => [cover.name, cover.logo ?? ""]));
  return (
    <OverlayTrigger
      placement="bottom"
      trigger={["hover", "focus"]}
      overlay={(props) => RenderDataSources(props, names, logosByName)}
    >
      {row}
    </OverlayTrigger>
  );
};

const InboxIdentity = ({ provider, email, covers }: InboxIdentityProps): JSX.Element => (
  <div className={styles.identity}>
    <ConnectionLogo
      logoUrl={PROVIDER_LOGO_URLS[provider]}
      className={styles.logo}
      fallback={<MdMailOutline aria-hidden="true" />}
    />
    <div className={styles.text}>
      <TruncateTooltip text={email} className={styles.email} />
      {covers ? <CoversRow covers={covers} /> : null}
    </div>
  </div>
);

export default InboxIdentity;
